import React, { useState, useEffect } from 'react';
import { RingProgress, Collapse, Badge, Paper, Loader } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import styles from './OrganisationalObjectives.module.scss';
import { Icon } from '../../../assets/icons/Icon';
import { useApiClient } from '../../../api/ApiClientContext';
import type { Api } from '../../../api/Api';


interface KpiData {
  id: string;
  name: string;
  current: string;
  target: string;
}

interface GoalData {
  id: string;
  title: string;
  kpis: number;
  initiatives: number;
  progress: number;
  status: 'ON TRACK' | 'BEHIND TARGET' | 'AT RISK';
  color: string;
}

interface ProgrammeData {
  name: string;
  activeUsers: number;
  cohorts: number;
  deliveryStreams: number;
}

interface ObjectiveData {
  id: string;
  title: string;
  goalsCount: number;
  kpisCount: number;
  initiative?: string;
  initiatives?: number;
  progress: number;
  status: 'on target' | 'KPI target' | 'behind';
  color: string;
  kpis: KpiData[];
  goals: GoalData[];
  supportedBy: ProgrammeData;
}

// API response types for expanded objective endpoint
interface ApiKpiActual {
  week_number: number;
  actual_value: number;
  recorded_at: string;
}

interface ApiKpi {
  id: string;
  title: string;
  unit: string;
  baseline: number | null;
  target: number | null;
  actuals: ApiKpiActual[];
  progress_percent: number | null;
}

interface ApiProgramme {
  id: string;
  name: string;
}

interface ApiGoal {
  id: string;
  title: string;
  is_active: boolean;
  kpis: ApiKpi[];
  programmes: ApiProgramme[];
  progress_percent: number | null;
}

interface ApiProgrammeStats {
  id: string;
  name: string;
  active_users: number;
  cohorts_count: number;
  delivery_stream_count: number;
}

interface ApiObjectiveExpanded {
  id: string;
  title: string;
  is_active: boolean;
  goals: ApiGoal[];
  direct_kpis: ApiKpi[];
  programme_stats: ApiProgrammeStats[];
  progress_percent: number | null;
}

const objectivesData: ObjectiveData[] = [
  {
    id: '1',
    title: 'Customer-centric & inclusive banking experience',
    goalsCount: 2,
    kpisCount: 3,
    initiative: 'Empathetic Leadership',
    progress: 67,
    status: 'on target',
    color: '#4C6EF5',
    kpis: [
      { id: '1', name: 'CUSTOMER TRUST INDEX', current: '74%', target: '80%' },
      { id: '2', name: 'DIGITAL NET PROMOTER SCORE', current: '+37', target: '+45' },
      { id: '3', name: 'CUSTOMER INCLUSION SATISFACTION', current: '2.7', target: '4.5' },
    ],
    goals: [
      { id: '1', title: 'Create deeper, fairer and more personalised customer relationships', kpis: 3, initiatives: 1, progress: 67, status: 'ON TRACK', color: '#4C6EF5' },
      { id: '2', title: 'Embed fairness and transparency in every customer decision', kpis: 2, initiatives: 1, progress: 50, status: 'ON TRACK', color: '#4C6EF5' },
    ],
    supportedBy: {
      name: 'Empathetic Leadership',
      activeUsers: 2106,
      cohorts: 185,
      deliveryStreams: 3,
    },
  },
  {
    id: '2',
    title: 'Accelerate digital & AI-led transformation',
    goalsCount: 2,
    kpisCount: 4,
    initiative: 'Be Secure',
    progress: 50,
    status: 'on target',
    color: '#228BE6',
    kpis: [
      { id: '1', name: 'AI ADOPTION RATE', current: '45%', target: '60%' },
      { id: '2', name: 'DIGITAL PROCESS AUTOMATION', current: '52%', target: '70%' },
      { id: '3', name: 'CLOUD MIGRATION PROGRESS', current: '68%', target: '85%' },
    ],
    goals: [
      { id: '1', title: 'Implement AI-driven customer service solutions', kpis: 2, initiatives: 1, progress: 45, status: 'ON TRACK', color: '#4C6EF5' },
      { id: '2', title: 'Automate 70% of manual processes', kpis: 2, initiatives: 1, progress: 55, status: 'ON TRACK', color: '#4C6EF5' },
    ],
    supportedBy: {
      name: 'Be Secure',
      activeUsers: 1250,
      cohorts: 95,
      deliveryStreams: 3,
    },
  },
  {
    id: '3',
    title: 'Simplify & strengthen operational resilience',
    goalsCount: 4,
    kpisCount: 4,
    initiative: 'Be Secure',
    progress: 75,
    status: 'on target',
    color: '#7950F2',
    kpis: [
      { id: '1', name: 'SYSTEM UPTIME', current: '99.8%', target: '99.9%' },
      { id: '2', name: 'INCIDENT RESPONSE TIME', current: '12min', target: '15min' },
      { id: '3', name: 'PROCESS EFFICIENCY SCORE', current: '82%', target: '90%' },
    ],
    goals: [
      { id: '1', title: 'Achieve 99.9% system availability', kpis: 1, initiatives: 1, progress: 85, status: 'ON TRACK', color: '#4C6EF5' },
      { id: '2', title: 'Reduce operational incidents by 40%', kpis: 1, initiatives: 1, progress: 72, status: 'ON TRACK', color: '#4C6EF5' },
      { id: '3', title: 'Streamline core banking processes', kpis: 1, initiatives: 1, progress: 68, status: 'ON TRACK', color: '#4C6EF5' },
      { id: '4', title: 'Enhance disaster recovery capabilities', kpis: 1, initiatives: 1, progress: 75, status: 'ON TRACK', color: '#4C6EF5' },
    ],
    supportedBy: {
      name: 'Be Secure',
      activeUsers: 890,
      cohorts: 62,
      deliveryStreams: 3,
    },
  },
  {
    id: '4',
    title: 'Develop high-performing, inclusive leadership culture',
    goalsCount: 2,
    kpisCount: 3,
    initiative: 'Empathetic Leadership',
    progress: 33,
    status: 'on target',
    color: '#0CA678',
    kpis: [
      { id: '1', name: 'LEADERSHIP DIVERSITY INDEX', current: '38%', target: '50%' },
      { id: '2', name: 'EMPLOYEE ENGAGEMENT SCORE', current: '72', target: '85' },
      { id: '3', name: 'INTERNAL PROMOTION RATE', current: '28%', target: '40%' },
    ],
    goals: [
      { id: '1', title: 'Increase leadership diversity to 50%', kpis: 1, initiatives: 1, progress: 38, status: 'BEHIND TARGET', color: '#FD7E14' },
      { id: '2', title: 'Improve employee engagement scores', kpis: 2, initiatives: 1, progress: 28, status: 'BEHIND TARGET', color: '#FD7E14' },
    ],
    supportedBy: {
      name: 'Empathetic Leadership',
      activeUsers: 1560,
      cohorts: 120,
      deliveryStreams: 3,
    },
  },
  {
    id: '5',
    title: 'Deliver sustainable growth & responsible banking',
    goalsCount: 2,
    kpisCount: 2,
    progress: 50,
    status: 'on target',
    color: '#15AABF',
    kpis: [
      { id: '1', name: 'SUSTAINABLE FINANCE VOLUME', current: '£2.1B', target: '£3.5B' },
      { id: '2', name: 'CARBON FOOTPRINT REDUCTION', current: '18%', target: '30%' },
    ],
    goals: [
      { id: '1', title: 'Grow sustainable finance portfolio to £3.5B', kpis: 1, initiatives: 1, progress: 60, status: 'ON TRACK', color: '#4C6EF5' },
      { id: '2', title: 'Reduce carbon footprint by 30%', kpis: 1, initiatives: 1, progress: 40, status: 'BEHIND TARGET', color: '#FD7E14' },
    ],
    supportedBy: {
      name: 'Sustainability Programme',
      activeUsers: 720,
      cohorts: 45,
      deliveryStreams: 3,
    },
  },
];

// Colors for ObjectiveCard badges and charts
const objectiveColors = ['#4C6EF5', '#228BE6', '#7950F2', '#0CA678', '#15AABF'];

// Expanded state colors mapping: { cardColor: { background, border } }
const expandedColorMap: Record<string, { background: string; border: string }> = {
  '#4C6EF5': { background: '#EDF2FF', border: '#4C6EF5' },
  '#228BE6': { background: '#E7F5FF', border: '#228BE6' },
  '#7950F2': { background: '#F3F0FF', border: '#7950F2' },
  '#0CA678': { background: '#E6FCF5', border: '#0CA678' },
  '#15AABF': { background: '#E3FAFC', border: '#15AABF' },
};

// Helper to format KPI value with units
const formatKpiValue = (value: number | null, unit: string): string => {
  if (value === null) return '-';
  if (unit.toLowerCase() === 'percent') {
    return `${(value * 100).toFixed(0)}%`;
  }
  if (unit.toLowerCase() === 'integer') {
    return value.toFixed(0);
  }
  return value.toFixed(2);
};

// Get latest actual value from measurements
const getLatestActual = (actuals: ApiKpiActual[]): number | null => {
  if (!actuals || actuals.length === 0) return null;
  // Sort by week_number descending and get first
  const sorted = [...actuals].sort((a, b) => b.week_number - a.week_number);
  return sorted[0].actual_value;
};

// Derive status from progress percent
const deriveStatus = (progress: number | null): 'ON TRACK' | 'BEHIND TARGET' | 'AT RISK' => {
  if (progress === null) return 'AT RISK';
  if (progress >= 80) return 'ON TRACK';
  if (progress >= 50) return 'BEHIND TARGET';
  return 'AT RISK';
};

// Derive objective status from progress percent
const deriveObjectiveStatus = (progress: number | null): 'on target' | 'KPI target' | 'behind' => {
  if (progress === null) return 'behind';
  if (progress >= 80) return 'on target';
  if (progress >= 50) return 'KPI target';
  return 'behind';
};

interface ObjectiveCardProps {
  objective: ObjectiveData;
  colorIndex: number;
  apiClient: Api<any>;
}

const ObjectiveCard: React.FC<ObjectiveCardProps> = ({ objective, colorIndex, apiClient }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedData, setExpandedData] = useState<ApiObjectiveExpanded | null>(null);
  const [isLoadingExpanded, setIsLoadingExpanded] = useState(false);
  const cardColor = objectiveColors[colorIndex % objectiveColors.length];
  const expandedColors = expandedColorMap[cardColor];

  // Fetch expanded data when card is expanded
  useEffect(() => {
    if (isExpanded && !expandedData && !isLoadingExpanded) {
      setIsLoadingExpanded(true);
      apiClient.http.request<ApiObjectiveExpanded, any>({
        path: `/api/v1/kpi/objectives/${objective.id}/expanded`,
        method: 'GET',
        secure: true,
      }).then(response => {
        setExpandedData(response.data);
      }).catch(error => {
        console.error('Error fetching expanded objective:', error);
      }).finally(() => {
        setIsLoadingExpanded(false);
      });
    }
  }, [isExpanded, expandedData, isLoadingExpanded, apiClient, objective.id]);

  // Transform expanded data to component format
  const displayKpis: KpiData[] = expandedData
    ? expandedData.goals.flatMap(g => g.kpis).slice(0, 4).map(kpi => ({
        id: kpi.id,
        name: kpi.title.toUpperCase(),
        current: formatKpiValue(getLatestActual(kpi.actuals) ?? kpi.baseline, kpi.unit),
        target: formatKpiValue(kpi.target, kpi.unit),
      }))
    : objective.kpis;

  const displayGoals: GoalData[] = expandedData
    ? expandedData.goals.map((g, idx) => ({
        id: g.id,
        title: g.title,
        kpis: g.kpis.length,
        initiatives: g.programmes.length,
        progress: g.progress_percent ?? 0,
        status: deriveStatus(g.progress_percent),
        color: objectiveColors[(colorIndex + idx) % objectiveColors.length],
      }))
    : objective.goals;

  const displayProgress = expandedData?.progress_percent ?? objective.progress;
  const displayStatus = expandedData
    ? deriveObjectiveStatus(expandedData.progress_percent)
    : objective.status;

  // Build supportedBy from programme_stats
  const displaySupportedBy: ProgrammeData = expandedData && expandedData.programme_stats.length > 0
    ? {
        name: expandedData.programme_stats.length === 1
          ? expandedData.programme_stats[0].name
          : `${expandedData.programme_stats.length} Programmes`,
        activeUsers: expandedData.programme_stats.reduce((sum, p) => sum + p.active_users, 0),
        cohorts: expandedData.programme_stats.reduce((sum, p) => sum + p.cohorts_count, 0),
        deliveryStreams: expandedData.programme_stats.reduce((sum, p) => sum + (p.delivery_stream_count || 0), 0),
      }
    : objective.supportedBy;

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'ON TRACK': return 'indigo';
      case 'BEHIND TARGET': return 'orange';
      case 'AT RISK': return 'red';
      default: return 'gray';
    }
  };

  const expandedStyle = isExpanded && expandedColors ? {
    backgroundColor: expandedColors.background,
    borderColor: expandedColors.border,
  } : {};

  return (
    <Paper 
      className={`${styles.objectiveCard} ${isExpanded ? styles['objectiveCard--expanded'] : ''}`} 
      radius="lg" 
      withBorder
      style={expandedStyle}
    >
      <div className={styles.objectiveCard__header} onClick={() => setIsExpanded(!isExpanded)}>
        <div className={styles.objectiveCard__content}>
          <Badge 
            className={styles.objectiveCard__badge}
            size="xs"
            variant="filled"
            style={{ backgroundColor: cardColor }}
          >
            STRATEGIC OBJECTIVE
          </Badge>
          <h3 className={styles.objectiveCard__title}>{objective.title}</h3>
          <p className={styles.objectiveCard__subtitle}>
            {objective.goalsCount} goals | {objective.kpisCount} KPIs
            {objective.initiatives ? ` | ${objective.initiatives} initiatives` : ''}
            {objective.initiative ? ` | ${objective.initiative}` : ''}
          </p>
        </div>

        <div className={styles.objectiveCard__right}>
          <div className={styles.objectiveCard__chartWrapper}>
            <RingProgress
              thickness={7}
              roundCaps
              rootColor="#E9ECEF"
              sections={[{ value: displayProgress ?? 0, color: cardColor }]}
              label={
                <div className={styles.objectiveCard__progressLabel}>
                  <span className={styles.objectiveCard__progressValue}>{displayProgress?.toFixed(0) ?? 0}%</span>
                  <span className={styles.objectiveCard__progressStatus}>{displayStatus}</span>
                </div>
              }
            />
          </div>
          <button 
            className={`${styles.objectiveCard__chevron} ${isExpanded ? styles['objectiveCard__chevron--expanded'] : ''}`}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      <Collapse in={isExpanded}>
        <div className={styles.objectiveCard__expandedContent}>
          {isLoadingExpanded ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
              <Loader size="md" />
            </div>
          ) : (
            <>
              {/* KPIs Section */}
              <div className={styles.objectiveCard__section}>
                <h4 className={styles.objectiveCard__sectionTitle}>KPIS</h4>
                <div className={styles.objectiveCard__kpisGrid}>
                  {displayKpis.map((kpi) => (
                    <Paper key={kpi.id} className={styles.kpiCard} withBorder radius="md">
                      <div className={styles.kpiCard__header}>
                        <span className={styles.kpiCard__name}>{kpi.name}</span>
                        <Icon iconName="GreyChartIcon" width="1rem" height="1rem" />
                      </div>
                      <div className={styles.kpiCard__values}>
                        <span className={styles.kpiCard__current}>{kpi.current}</span>
                        <span className={styles.kpiCard__separator}>/</span>
                        <span className={styles.kpiCard__target}>{kpi.target}</span>
                      </div>
                    </Paper>
                  ))}
                </div>
              </div>

              {/* Goals Section */}
              <div className={styles.objectiveCard__section}>
                <h4 className={styles.objectiveCard__sectionTitle}>GOALS</h4>
                <div className={styles.objectiveCard__goalsGrid}>
                  {displayGoals.map((goal) => (
                    <Paper key={goal.id} className={styles.goalCard} withBorder radius="md">
                      <div className={styles.goalCard__content}>
                        <h5 className={styles.goalCard__title}>{goal.title}</h5>
                        <p className={styles.goalCard__subtitle}>{goal.kpis} KPIs | {goal.initiatives} initiative{goal.initiatives !== 1 ? 's' : ''}</p>
                      </div>
                      <div className={styles.goalCard__progress}>
                        <RingProgress
                          size={56}
                          thickness={4}
                          roundCaps
                          rootColor="#E9ECEF"
                          sections={[{ value: goal.progress, color: goal.color }]}
                          label={
                            <span className={styles.goalCard__progressValue}>{goal.progress.toFixed(0)}%</span>
                          }
                        />
                        <Badge
                          size="xs"
                          variant="light"
                          color={getStatusBadgeColor(goal.status)}
                          className={styles.goalCard__statusBadge}
                        >
                          {goal.status}
                        </Badge>
                      </div>
                    </Paper>
                  ))}
                </div>
              </div>

              {/* Supported By Section */}
              <div className={styles.objectiveCard__section}>
                <h4 className={styles.objectiveCard__sectionTitle}>SUPPORTED BY</h4>
                <Paper className={styles.supportedByCard} withBorder radius="md">
                  <div className={styles.supportedByCard__header}>
                    <h5 className={styles.supportedByCard__name}>{displaySupportedBy.name}</h5>
                  </div>
                  <p className={styles.supportedByCard__info}>
                    {displaySupportedBy.activeUsers} active users | {displaySupportedBy.cohorts} cohorts | {displaySupportedBy.deliveryStreams} delivery streams
                  </p>
                </Paper>
              </div>
            </>
          )}
        </div>
      </Collapse>
    </Paper>
  );
};

export interface QuickInsightCardProps {
  title: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
}

export const QuickInsightCard: React.FC<QuickInsightCardProps> = ({ title, children, icon }) => (
  <Paper className={styles.insightCard} radius="md" withBorder>
    <div className={styles.insightCard__header}>
      <span className={styles.insightCard__title}>{title}</span>
      {icon && <span className={styles.insightCard__icon}>{icon}</span>}
    </div>
    {children}
  </Paper>
);

interface OrganisationalObjectivesProps {
  className?: string;
}

// Transform API response to component's expected format
const transformApiObjectives = (apiObjectives: any[]): ObjectiveData[] => {
  return apiObjectives.map((obj, index) => {
    const goals = obj.goals || [];
    const allKpis = goals.flatMap((g: any) => g.kpis || []);

    return {
      id: obj.id,
      title: obj.title,
      goalsCount: obj.goal_count || goals.length,
      kpisCount: obj.kpi_count || allKpis.length,
      progress: 0, // Overridden by progressMap
      status: 'on target' as const,
      color: objectiveColors[index % objectiveColors.length],
      kpis: allKpis.slice(0, 3).map((kpi: any) => ({
        id: kpi.id,
        name: kpi.title?.toUpperCase() || 'KPI',
        current: kpi.baseline?.toString() || '0',
        target: kpi.target?.toString() || '0',
      })),
      goals: goals.map((goal: any, gIndex: number) => ({
        id: goal.id,
        title: goal.title,
        kpis: goal.kpis?.length || 0,
        initiatives: 1,
        progress: 0, // Overridden on expand
        status: 'ON TRACK' as const,
        color: objectiveColors[(index + gIndex) % objectiveColors.length],
      })),
      supportedBy: {
        name: '',
        activeUsers: 0,
        cohorts: 0,
        deliveryStreams: 0,
      },
    };
  });
};

// Response type for insights API
interface MostImprovedKPIApiResponse {
  name: string;
  current_value: number;
  improvement_percent: number;
  trend: 'up' | 'down' | 'flat';
}

interface HabitsLoggedApiResponse {
  count: number;
  avg_per_user: number;
  change_percent: number;
  trend: 'up' | 'down' | 'flat';
}

interface KPIInsightsApiResponse {
  kpis_on_target: number;
  total_kpis: number;
  change_percent: number;
  trend: 'up' | 'down' | 'flat';
  most_improved_kpi: MostImprovedKPIApiResponse | null;
  habits_logged: HabitsLoggedApiResponse | null;
}

// Response type for progress API
interface GoalProgressApiResponse {
  id: string;
  title: string;
  kpis_on_target: number;
  total_kpis: number;
  progress_percent: number | null;
}

interface ObjectiveProgressApiResponse {
  id: string;
  title: string;
  kpis_on_target: number;
  total_kpis: number;
  progress_percent: number | null;
  goals: GoalProgressApiResponse[];
}

interface AllObjectivesProgressApiResponse {
  objectives: ObjectiveProgressApiResponse[];
}

export const OrganisationalObjectives: React.FC<OrganisationalObjectivesProps> = ({ className = '' }) => {
  const apiClient = useApiClient();

  // Fetch objectives from KPI API
  const { data: apiObjectives, isLoading, error } = useQuery({
    queryKey: ['kpi-objectives'],
    queryFn: async () => {
      const response = await apiClient.http.request<any[], any>({
        path: '/api/v1/kpi/objectives',
        method: 'GET',
        secure: true,
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch KPI insights for Quick Insights section
  const { data: kpiInsights } = useQuery({
    queryKey: ['kpi-insights'],
    queryFn: async () => {
      const response = await apiClient.http.request<KPIInsightsApiResponse, any>({
        path: '/api/v1/kpi/insights',
        method: 'GET',
        secure: true,
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch progress data for all objectives (BDD-based calculation)
  const { data: progressData } = useQuery({
    queryKey: ['kpi-objectives-progress'],
    queryFn: async () => {
      const response = await apiClient.http.request<AllObjectivesProgressApiResponse, any>({
        path: '/api/v1/kpi/objectives/progress',
        method: 'GET',
        secure: true,
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Build progress map from progress data
  const progressMap = new Map<string, ObjectiveProgressApiResponse>();
  if (progressData?.objectives) {
    for (const obj of progressData.objectives) {
      progressMap.set(obj.id, obj);
    }
  }

  // Transform API data with progress, or use fallback mock data
  const objectives = apiObjectives
    ? transformApiObjectives(apiObjectives).map(obj => {
        const progress = progressMap.get(obj.id);
        if (progress) {
          return {
            ...obj,
            progress: progress.progress_percent ?? 0,
            status: deriveObjectiveStatus(progress.progress_percent),
          };
        }
        return obj;
      })
    : objectivesData;

  // Use insights API data or fallback to calculated values
  const kpisOnTarget = kpiInsights?.kpis_on_target ?? 0;
  const totalKpis = kpiInsights?.total_kpis ?? objectives.reduce((sum, obj) => sum + obj.kpisCount, 0);
  const changePercent = kpiInsights?.change_percent ?? 0;
  const changeTrend = kpiInsights?.trend ?? 'flat';

  // Build most improved KPI from API data
  const mostImprovedKpiData = kpiInsights?.most_improved_kpi
    ? {
        name: kpiInsights.most_improved_kpi.name,
        value: kpiInsights.most_improved_kpi.current_value,
        change: kpiInsights.most_improved_kpi.improvement_percent,
        trend: kpiInsights.most_improved_kpi.trend,
        comparedTo: 'Compared to previous measurement',
      }
    : null;

  // Build habits logged from API data
  const habitsLoggedData = kpiInsights?.habits_logged
    ? {
        count: kpiInsights.habits_logged.count,
        change: kpiInsights.habits_logged.change_percent,
        trend: kpiInsights.habits_logged.trend,
        subtitle: `Avg: ${kpiInsights.habits_logged.avg_per_user.toFixed(1)} per user`,
      }
    : null;

  const quickInsights = {
    kpisOnTarget: {
      current: kpisOnTarget,
      total: totalKpis,
      change: changePercent,
      trend: changeTrend,
      comparedTo: 'Compared to previous week',
    },
    mostImprovedKpi: mostImprovedKpiData,
    habitsLogged: habitsLoggedData,
  };

  if (isLoading) {
    return (
      <div className={`${styles.organisationalObjectives} ${className}`} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Loader size="lg" />
      </div>
    );
  }

  if (error) {
    console.error('Error fetching objectives:', error);
    // Fall through to render with mock data
  }

  return (
    <div className={`${styles.organisationalObjectives} ${className}`}>
      {/* Quick insights section */}
      <div className={styles.quickInsights}>
        {/* <div className={styles.quickInsights__header}>
          <h2 className={styles.quickInsights__title}>Quick insights</h2>
          <button className={styles.quickInsights__viewDetails}>View details</button>
        </div> */}

        <div className={styles.quickInsights__cards}>
          <QuickInsightCard title="KPIS ON TARGET">
            <div className={styles.insightCard__row}>
              <span className={styles.insightCard__mainValue}>
                {quickInsights.kpisOnTarget.current} / {quickInsights.kpisOnTarget.total}
              </span>
              <div className={`${styles.insightCard__change} ${
                quickInsights.kpisOnTarget.trend === 'up' ? styles['insightCard__change--up'] :
                quickInsights.kpisOnTarget.trend === 'down' ? styles['insightCard__change--down'] : ''
              }`}>
                {quickInsights.kpisOnTarget.change}% <Icon
                  iconName={
                    quickInsights.kpisOnTarget.trend === 'up' ? 'GreenUpIcon' :
                    quickInsights.kpisOnTarget.trend === 'down' ? 'ArrowDownIcon' : 'BlueArrowRight'
                  }
                  width="0.5rem"
                  height="0.5rem"
                />
              </div>
            </div>
            <span className={styles.insightCard__subtitle}>{quickInsights.kpisOnTarget.comparedTo}</span>
          </QuickInsightCard>

          <QuickInsightCard title="MOST IMPROVED KPI">
            {quickInsights.mostImprovedKpi ? (
              <>
                <span className={styles.insightCard__kpiName}>{quickInsights.mostImprovedKpi.name}</span>
                <div className={styles.insightCard__row}>
                  <span className={styles.insightCard__mainValue}>
                    {typeof quickInsights.mostImprovedKpi.value === 'number' && quickInsights.mostImprovedKpi.value < 1
                      ? `${(quickInsights.mostImprovedKpi.value * 100).toFixed(0)}%`
                      : quickInsights.mostImprovedKpi.value}
                  </span>
                  <div className={`${styles.insightCard__change} ${
                    quickInsights.mostImprovedKpi.trend === 'up' ? styles['insightCard__change--up'] :
                    quickInsights.mostImprovedKpi.trend === 'down' ? styles['insightCard__change--down'] : ''
                  }`}>
                    {quickInsights.mostImprovedKpi.change}% <Icon
                      iconName={
                        quickInsights.mostImprovedKpi.trend === 'up' ? 'GreenUpIcon' :
                        quickInsights.mostImprovedKpi.trend === 'down' ? 'RedDownWithBGIcon' : 'BlueArrowRight'
                      }
                      width="0.5rem"
                      height="0.5rem"
                    />
                  </div>
                </div>
                <span className={styles.insightCard__subtitle}>{quickInsights.mostImprovedKpi.comparedTo}</span>
              </>
            ) : (
              <span className={styles.insightCard__subtitle}>No improvement data available</span>
            )}
          </QuickInsightCard>

          <QuickInsightCard title="HABITS LOGGED THIS WEEK">
            {quickInsights.habitsLogged ? (
              <>
                <div className={styles.insightCard__row}>
                  <span className={styles.insightCard__mainValue}>
                    {quickInsights.habitsLogged.count.toLocaleString()}
                  </span>
                  <div className={`${styles.insightCard__change} ${
                    quickInsights.habitsLogged.trend === 'up' ? styles['insightCard__change--up'] :
                    quickInsights.habitsLogged.trend === 'down' ? styles['insightCard__change--down'] : ''
                  }`}>
                    {quickInsights.habitsLogged.change}% <Icon
                      iconName={
                        quickInsights.habitsLogged.trend === 'up' ? 'GreenUpIcon' :
                        quickInsights.habitsLogged.trend === 'down' ? 'RedDownWithBGIcon' : 'BlueArrowRight'
                      }
                      width="0.5rem"
                      height="0.5rem"
                    />
                  </div>
                </div>
                <span className={styles.insightCard__subtitle}>{quickInsights.habitsLogged.subtitle}</span>
              </>
            ) : (
              <span className={styles.insightCard__subtitle}>No data available</span>
            )}
          </QuickInsightCard>
        </div>
      </div>

      {/* Objectives section */}
      <div className={styles.objectives}>
        <div className={styles.objectives__header}>
          <h2 className={styles.objectives__title}>Organisational objectives</h2>
          <span className={styles.objectives__count}>{objectives.length} objectives</span>
        </div>

        <div className={styles.objectives__list}>
          {objectives.map((objective, index) => (
            <ObjectiveCard key={objective.id} objective={objective} colorIndex={index} apiClient={apiClient} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default OrganisationalObjectives;
